import { useIdentityStore } from '@/store/identity.store';
import { HttpTransportType, HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { onUnmounted, ref, watch } from 'vue';

export function useSignalR(url: string, events: Record<string, (data: any) => void>) {
    const active = ref<boolean>(false);
    const connection = ref<HubConnection | null>(null);
    const isStarting = ref<boolean>(false);
    const identityStore = useIdentityStore();
    const hasCredentials = () => Boolean(identityStore.data);
    const buildConnection = () => {
        const conn = new HubConnectionBuilder()
            .withUrl(import.meta.env.VITE_BACKEND_URL + url, {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
                withCredentials: true,
            })
            .build();

        // Register handlers
        Object.entries(events).forEach(([event, handler]) => {
            conn.on(event, handler);
        });

        conn.onclose(async () => {
            await attemptStartConnection();
        });

        return conn;
    };

    const attemptStartConnection = async () => {
        if (!active.value || !hasCredentials()) return;
        if (!connection.value) {
            connection.value = buildConnection();
        }
        if (connection.value.state !== 'Disconnected' || isStarting.value) return;

        try {
            isStarting.value = true;
            await connection.value.start();
        } catch (error) {
            console.error('SignalR connection error:', error);
        } finally {
            isStarting.value = false;
        }
    };

    const startConnection = async () => {
        active.value = true;
        await attemptStartConnection();
    };

    const disconnectConnection = async () => {
        if (connection.value) {
            await connection.value.stop();
            connection.value = null;
        }
    };

    const stopConnection = async () => {
        active.value = false;
        await disconnectConnection();
    };

    watch(
        () => identityStore.data,
        async (data) => {
            if (!active.value) return;
            if (data) {
                await attemptStartConnection();
                return;
            }

            await disconnectConnection();
        }
    );
    onUnmounted(stopConnection);

    return {
        connection,
        startConnection,
        stopConnection,
    };
}
